import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Drawer, Modal, StatusBadge, Toggle, ConfirmDialog, EmptyState, Select } from '@/components/ui';
import { Icon, type IconName } from '@/components/ui/Icon';
import { adminApi, type HubAutomation, type HubCatalog, type HubAutomationRun, type HubAutomationTestResult, type HubConditions } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { fmtDateTime, JsonDetails } from './common';
import { AutomationBuilder, type BuilderPrefill } from './AutomationBuilder';
import { ACTION_META, TRIGGER_ICON, countConditions } from './automationMeta';

function runStatusTone(s?: string | null): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (s === 'success') return 'ok';
  if (s === 'partial') return 'warn';
  if (s === 'failed') return 'danger';
  return 'neutral';
}
const RUN_STATUS_HE: Record<string, string> = { success: 'הצליח', partial: 'חלקי', failed: 'נכשל' };

function triggerLabel(a: HubAutomation, catalog: HubCatalog): string {
  if (a.trigger_type === 'event') {
    const ev = a.trigger_config?.event;
    if (ev === '*') return 'כל האירועים';
    return typeof ev === 'string' ? (catalog.events[ev] ?? ev) : 'אירוע';
  }
  if (a.trigger_type === 'inbound') return 'כתובת נכנסת (Webhook)';
  if (a.trigger_type === 'schedule') {
    const cron = a.trigger_config?.cron;
    return typeof cron === 'string' ? `מתוזמן · ${cron}` : 'מתוזמן';
  }
  return a.trigger_type;
}

type FilterKind = 'all' | 'active' | 'paused' | 'event' | 'inbound' | 'schedule';

export function AutomationsTab({
  automations,
  catalog,
  onChanged,
  builderPrefill,
  onConsumePrefill,
}: {
  automations: HubAutomation[];
  catalog: HubCatalog;
  onChanged: () => void;
  builderPrefill?: BuilderPrefill | null;
  onConsumePrefill?: () => void;
}) {
  const [builder, setBuilder] = useState<HubAutomation | 'new' | null>(null);
  const [runsFor, setRunsFor] = useState<HubAutomation | null>(null);
  const [testFor, setTestFor] = useState<HubAutomation | null>(null);
  const [confirmDel, setConfirmDel] = useState<HubAutomation | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [filter, setFilter] = useState<FilterKind>('all');

  // Open the builder pre-filled from an event-log "create automation" click.
  useEffect(() => {
    if (builderPrefill) setBuilder('new');
  }, [builderPrefill]);

  const filtered = useMemo(() => automations.filter((a) => {
    switch (filter) {
      case 'active': return a.active;
      case 'paused': return !a.active;
      case 'event': case 'inbound': case 'schedule': return a.trigger_type === filter;
      default: return true;
    }
  }), [automations, filter]);

  const counts = useMemo(() => ({
    total: automations.length,
    active: automations.filter((a) => a.active).length,
  }), [automations]);

  async function toggleActive(a: HubAutomation, active: boolean) {
    setBusyId(a.id);
    try {
      await adminApi.updateHubAutomation(a.id, { active });
      onChanged();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  async function duplicate(a: HubAutomation) {
    setBusyId(a.id);
    try {
      await adminApi.createHubAutomation({
        name: `${a.name} (עותק)`,
        trigger_type: a.trigger_type,
        // inbound gets a fresh server-generated token; event/schedule copy config.
        trigger_config: a.trigger_type === 'inbound' ? {} : a.trigger_config,
        conditions: a.conditions as HubConditions,
        actions: a.actions,
        active: false, // clones start paused so they don't fire before review
      });
      toast('שוכפל (מושהה)', 'ok');
      onChanged();
    } catch {
      toast('שגיאה בשכפול', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  async function del(a: HubAutomation) {
    try {
      await adminApi.deleteHubAutomation(a.id);
      toast('נמחק', 'ok');
      setConfirmDel(null);
      onChanged();
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  function closeBuilder() {
    setBuilder(null);
    onConsumePrefill?.();
  }

  const filterOpts: { value: FilterKind; label: string }[] = [
    { value: 'all', label: `הכל (${counts.total})` },
    { value: 'active', label: `פעילות (${counts.active})` },
    { value: 'paused', label: `מושהות (${counts.total - counts.active})` },
    { value: 'event', label: 'לפי אירוע' },
    { value: 'schedule', label: 'מתוזמנות' },
    { value: 'inbound', label: 'כתובת נכנסת' },
  ];

  return (
    <div className="stack" style={{ gap: 'var(--gap)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 180 }}>
          <Select value={filter} onChange={(v) => setFilter(v as FilterKind)} options={filterOpts} />
        </div>
        <button type="button" className="btn primary" onClick={() => setBuilder('new')}>
          <Icon name="plus" size={14} /> אוטומציה חדשה
        </button>
      </div>

      {automations.length === 0 ? (
        <Card>
          <EmptyState
            icon="bolt"
            title="אין עדיין אוטומציות"
            description="אוטומציה מגיבה אוטומטית לאירוע במערכת — למשל שולחת WhatsApp כששיחה לא נענתה, או פותחת טיקט כשסנטימנט השיחה שלילי."
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button type="button" className="btn primary" onClick={() => setBuilder('new')}>
              <Icon name="plus" size={14} /> בנה אוטומציה ראשונה
            </button>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card><div style={{ color: 'var(--muted)', textAlign: 'center', padding: 12 }}>אין אוטומציות בסינון הזה.</div></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'var(--gap)' }}>
          {filtered.map((a) => (
            <AutomationCard
              key={a.id}
              a={a}
              catalog={catalog}
              busy={busyId === a.id}
              onToggle={(v) => toggleActive(a, v)}
              onEdit={() => setBuilder(a)}
              onRuns={() => setRunsFor(a)}
              onTest={() => setTestFor(a)}
              onDuplicate={() => duplicate(a)}
              onDelete={() => setConfirmDel(a)}
            />
          ))}
        </div>
      )}

      {builder && (
        <AutomationBuilder
          automation={builder === 'new' ? null : builder}
          prefill={builder === 'new' ? (builderPrefill ?? undefined) : undefined}
          catalog={catalog}
          onClose={closeBuilder}
          onSaved={() => { closeBuilder(); onChanged(); }}
        />
      )}

      {runsFor && <RunsDrawer automation={runsFor} onClose={() => setRunsFor(null)} />}
      {testFor && <TestModal automation={testFor} onClose={() => setTestFor(null)} />}

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => { if (confirmDel) void del(confirmDel); }}
        title="מחיקת אוטומציה"
        message={`למחוק את "${confirmDel?.name}"? הפעולה אינה הפיכה.`}
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}

// ── כרטיס אוטומציה ────────────────────────────────────────────────────────────
function AutomationCard({
  a, catalog, busy, onToggle, onEdit, onRuns, onTest, onDuplicate, onDelete,
}: {
  a: HubAutomation;
  catalog: HubCatalog;
  busy: boolean;
  onToggle: (v: boolean) => void;
  onEdit: () => void;
  onRuns: () => void;
  onTest: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const ev = typeof a.trigger_config?.event === 'string' ? a.trigger_config.event : undefined;
  const trigIcon: IconName = TRIGGER_ICON[a.trigger_type] ?? 'bolt';
  const condCount = countConditions(a.conditions);
  const actionCount = a.actions?.length ?? 0;

  return (
    <Card padded={false} style={{ overflow: 'hidden', opacity: a.active ? 1 : 0.72, transition: 'opacity 120ms' }}>
      {/* פס עליון: אייקון טריגר + שם + Toggle */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 'var(--pad-card)', borderBottom: '1px solid var(--line)' }}>
        <span style={{
          display: 'inline-flex', width: 34, height: 34, flexShrink: 0, borderRadius: 'var(--r-2)',
          background: a.active ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface-2)',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={trigIcon} size={17} color={a.active ? 'var(--accent)' : 'var(--muted)'} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--t-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name || '—'}</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {triggerLabel(a, catalog)}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()} title={a.active ? 'פעילה' : 'מושהית'}>
          <Toggle checked={a.active} onChange={onToggle} disabled={busy} />
        </div>
      </div>

      {/* גוף: שרשרת טריגר → תנאים → פעולות */}
      <div style={{ padding: 'var(--pad-card)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FlowChain
          triggerLabel={ev && ev !== '*' ? (catalog.events[ev] ?? ev) : triggerLabel(a, catalog)}
          triggerIcon={trigIcon}
          condCount={condCount}
          actions={(a.actions ?? []).map((ac) => ({
            label: catalog.actions[ac.type] ?? ac.type,
            icon: ACTION_META[ac.type]?.icon ?? 'bolt',
            delayed: Number((ac.config as { delay?: unknown })?.delay ?? 0) > 0,
          }))}
        />

        {/* פס תחתון: סטטוס ריצה אחרונה + מונה */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          {a.last_status && (
            <StatusBadge tone={runStatusTone(a.last_status)} size="sm">{RUN_STATUS_HE[a.last_status] ?? a.last_status}</StatusBadge>
          )}
          <span>{(a.runs_count ?? 0)} ריצות</span>
          {a.last_run_at && <span>· אחרונה {fmtDateTime(a.last_run_at)}</span>}
          {condCount > 0 && <span>· {condCount} תנאים</span>}
          <span>· {actionCount} פעולות</span>
        </div>
      </div>

      {/* פעולות כרטיס */}
      <div style={{ display: 'flex', gap: 2, borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
        <CardBtn icon="edit" label="עריכה" onClick={onEdit} />
        <CardBtn icon="bolt" label="בדיקה" onClick={onTest} />
        <CardBtn icon="history" label="היסטוריה" onClick={onRuns} />
        <CardBtn icon="copy" label="שכפול" onClick={onDuplicate} disabled={busy} />
        <CardBtn icon="trash" label="מחיקה" onClick={onDelete} danger />
      </div>
    </Card>
  );
}

function CardBtn({ icon, label, onClick, danger, disabled }: { icon: IconName; label: string; onClick: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '9px 4px', border: 'none', background: 'transparent', cursor: disabled ? 'default' : 'pointer',
        color: danger ? 'var(--danger)' : 'var(--text-2, var(--muted))', fontSize: 'var(--t-xs)',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon name={icon} size={14} /> {label}
    </button>
  );
}

// שרשרת ויזואלית: טריגר → [תנאים] → פעולות
function FlowChain({
  triggerLabel: trig, triggerIcon, condCount, actions,
}: {
  triggerLabel: string;
  triggerIcon: IconName;
  condCount: number;
  actions: { label: string; icon: IconName; delayed: boolean }[];
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 'var(--t-xs)' }}>
      <Pill icon={triggerIcon} tone="accent">{trig}</Pill>
      <Icon name="arrowL" size={12} color="var(--muted)" />
      {condCount > 0 && (
        <>
          <Pill icon="filter" tone="neutral">{condCount} תנאים</Pill>
          <Icon name="arrowL" size={12} color="var(--muted)" />
        </>
      )}
      {actions.length === 0 ? (
        <Pill icon="warn" tone="warn">ללא פעולות</Pill>
      ) : (
        actions.slice(0, 3).map((ac, i) => (
          <Pill key={i} icon={ac.icon} tone="neutral" badge={ac.delayed ? 'clock' : undefined}>{ac.label}</Pill>
        ))
      )}
      {actions.length > 3 && <span style={{ color: 'var(--muted)' }}>+{actions.length - 3}</span>}
    </div>
  );
}

function Pill({ icon, tone, children, badge }: { icon: IconName; tone: 'accent' | 'neutral' | 'warn'; children: React.ReactNode; badge?: IconName }) {
  const bg = tone === 'accent' ? 'var(--accent-soft, var(--surface-2))' : tone === 'warn' ? 'var(--surface-2)' : 'var(--surface-2)';
  const fg = tone === 'accent' ? 'var(--accent)' : tone === 'warn' ? 'var(--warn)' : 'var(--text-2, var(--muted))';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 'var(--r-pill)', background: bg, color: fg, border: '1px solid var(--line)', maxWidth: 160 }}>
      <Icon name={icon} size={12} color={fg} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
      {badge && <Icon name={badge} size={11} color="var(--muted)" />}
    </span>
  );
}

// ── בדיקה יבשה + היסטוריה (ללא שינוי מהותי) ──────────────────────────────────
function StepList({ steps }: { steps: { action: string; ok: boolean; detail?: string; deferred?: boolean }[] }) {
  if (steps.length === 0) return <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין שלבים.</div>;
  return (
    <div className="stack" style={{ gap: 4 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--t-sm)' }}>
          <Icon name={s.deferred ? 'clock' : s.ok ? 'ok' : 'danger'} size={14} color={s.deferred ? 'var(--muted)' : s.ok ? 'var(--ok)' : 'var(--danger)'} />
          <span>{s.action}</span>
          {s.detail && <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{s.detail}</span>}
        </div>
      ))}
    </div>
  );
}

function TestModal({ automation, onClose }: { automation: HubAutomation; onClose: () => void }) {
  const [result, setResult] = useState<HubAutomationTestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const run = useCallback(() => {
    setLoading(true);
    setError(false);
    adminApi.testHubAutomation(automation.id)
      .then(setResult)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [automation.id]);

  useEffect(() => { run(); }, [run]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`בדיקה יבשה · ${automation.name}`}
      footer={<>
        <button type="button" className="btn" onClick={run} disabled={loading}>הרץ שוב</button>
        <button type="button" className="btn primary" onClick={onClose}>סגור</button>
      </>}
    >
      {loading ? (
        <div style={{ color: 'var(--muted)' }}>מריץ בדיקה…</div>
      ) : error ? (
        <div style={{ color: 'var(--danger)' }}>שגיאה בהרצת הבדיקה.</div>
      ) : result ? (
        <div className="stack">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>תנאים:</span>
            <StatusBadge tone={result.conditions_passed ? 'ok' : 'neutral'} size="sm">
              {result.conditions_passed ? 'עברו — הפעולות ירוצו' : 'לא עברו — הפעולות ידולגו'}
            </StatusBadge>
          </div>
          <div className="stack" style={{ gap: 6 }}>
            <strong style={{ fontSize: 'var(--t-sm)' }}>שלבים</strong>
            <StepList steps={result.steps ?? []} />
          </div>
          <JsonDetails label="Payload לדוגמה (מהאירוע האחרון שנרשם)" value={result.sample_payload} />
        </div>
      ) : null}
    </Modal>
  );
}

function RunsDrawer({ automation, onClose }: { automation: HubAutomation; onClose: () => void }) {
  const [rows, setRows] = useState<HubAutomationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    adminApi.hubAutomationRuns(automation.id)
      .then(setRows)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [automation.id]);

  useEffect(() => { load(); }, [load]);

  // שליחה חוזרת ידנית (אפיון 15.2). אחרי הריצה טוענים מחדש כדי שהסטטוס
  // ומונה הניסיונות יהיו של השרת ולא ניחוש אופטימי של הדפדפן.
  const retry = useCallback(async (runId: number) => {
    setRetrying(runId);
    setRetryError(null);
    try {
      await adminApi.hubAutomationRetryRun(automation.id, runId);
      load();
    } catch {
      setRetryError('השליחה החוזרת נכשלה. נסו שוב עוד רגע.');
    } finally {
      setRetrying(null);
    }
  }, [automation.id, load]);

  return (
    <Drawer isOpen onClose={onClose} title={`היסטוריית ריצות · ${automation.name}`} width={560}>
      {loading ? (
        <div style={{ color: 'var(--muted)' }}>טוען…</div>
      ) : error ? (
        <div className="stack"><div style={{ color: 'var(--danger)' }}>שגיאה בטעינה.</div><button type="button" className="btn" onClick={load}>נסה שוב</button></div>
      ) : rows.length === 0 ? (
        <EmptyState icon="history" title="אין ריצות עדיין" description="ריצות יופיעו כאן ברגע שהאוטומציה תופעל על ידי אירוע תואם." />
      ) : (
        <div className="stack">
          {retryError && <div style={{ color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>{retryError}</div>}
          {rows.map((r) => (
            <Card key={r.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <StatusBadge tone={runStatusTone(r.status)} size="sm">{RUN_STATUS_HE[r.status] ?? r.status}</StatusBadge>
                  {r.trigger_event && <span style={{ fontSize: 'var(--t-sm)' }}>{r.trigger_event}</span>}
                  {r.duration_ms != null && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.duration_ms}ms</span>}
                </div>
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{fmtDateTime(r.created_at)}</span>
              </div>

              {/* ניסיונות ומועד הניסיון הבא — כדי שכשל לא ייראה כמו סוף הדרך */}
              {(r.attempts != null && r.attempts > 1) || r.next_retry_at ? (
                <div style={{ marginTop: 6, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                  {r.attempts != null && r.attempts > 1 && <span>ניסיון {r.attempts} מתוך 3</span>}
                  {r.attempts != null && r.attempts > 1 && r.next_retry_at && <span> · </span>}
                  {r.next_retry_at && <span>ניסיון הבא: {fmtDateTime(r.next_retry_at)}</span>}
                </div>
              ) : null}

              <div style={{ marginTop: 8 }}>
                <StepList steps={r.steps ?? []} />
              </div>

              {/* השרת מחליט מה ניתן לשלוח שוב — לא הסטטוס בצד הלקוח */}
              {r.can_retry && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={retrying === r.id}
                    onClick={() => retry(r.id)}
                  >
                    {retrying === r.id ? 'שולח…' : 'שלח שוב'}
                  </button>
                </div>
              )}
              <div style={{ marginTop: 6 }}>
                <JsonDetails label="Payload" value={r.trigger_snapshot} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </Drawer>
  );
}
